import React, { useMemo, useState, useRef, type ReactNode } from 'react';
import { Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { ActionName, type ActionItem } from '@atlaskit/smart-card';
import { useIntl } from '@atlassian/jira-intl';
import { PopupTrigger } from '@atlassian/entry-point-popup';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { useProjectUrlConstructor } from '../../util/index.tsx';
import { inheritancePopupEntryPoint } from '../inheritance-popup/entrypoint.tsx';
import { InheritancePopupSkeleton } from '../inheritance-popup/fallbacks/skeleton.tsx';
import { InheritancePopupErrorFallback } from '../inheritance-popup/fallbacks/error-fallback.tsx';
import { RestrictedAlikeSmartLink } from '../restricted/index.tsx';
import { SimpleInlineSmartLink } from './inline-smart-llink/index.tsx';
import messages from './messages.tsx';

const styles = cssMap({
	container: {
		paddingInlineEnd: token('space.100'),
	},
});

const EntryPointWrapper = ({
	children,
	onClose,
	onInheritancePopupOpen,
	issueAri,
	linkedProject,
}: {
	children: ReactNode;
	onClose?: () => void;
	onInheritancePopupOpen?: () => void;
	issueAri: string;
	linkedProject: {
		projectUrl: string | null;
		isRestricted: boolean;
	};
}): React.JSX.Element => {
	return (
		<PopupTrigger
			entryPoint={inheritancePopupEntryPoint}
			fallback={<InheritancePopupSkeleton />}
			errorFallback={InheritancePopupErrorFallback}
			entryPointProps={{ onInheritancePopupOpen, issueAri, linkedProject }}
			entryPointParams={{ issueId: issueAri }}
			onClose={onClose}
			trigger={(triggerProps) => (
				<Box
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...triggerProps}
				>
					{children}
				</Box>
			)}
			placement="bottom"
			zIndex={600}
		/>
	);
};

type AtlasProjectSmartLinkProps = {
	projectAri: string;
	projectKey?: string | null;
	name?: string | null;
	isPrivate: boolean;
	isInherited: boolean;
	issueAri?: string | null;
	initialHasNoAccess: boolean;
	onHandleNoAccess?: () => void;
};

export const AtlasProjectSmartLink = ({
	projectAri,
	projectKey,
	isInherited,
	isPrivate,
	issueAri,
	initialHasNoAccess,
	onHandleNoAccess,
	name,
}: AtlasProjectSmartLinkProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const constructAtlasProjectUrl = useProjectUrlConstructor();
	const [isHovered, setIsHovered] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const [isPinned, setIsPinned] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const containerRef = useRef<HTMLDivElement>(null);

	const shouldShowActions = isInherited && (isHovered || isFocused || isPinned);

	const projectUrl = useMemo(
		() => (projectKey ? constructAtlasProjectUrl({ ari: projectAri, projectKey }) : null),
		[constructAtlasProjectUrl, projectAri, projectKey],
	);

	const actions: ActionItem[] | undefined = useMemo(() => {
		if (!issueAri || !isInherited) {
			return [];
		}
		return [
			{
				name: ActionName.CustomAction,
				onClick: () => {
					fireUIAnalytics(
						createAnalyticsEvent({
							action: 'clicked',
							actionSubject: 'inheritedButton',
						}),
					);
				},
				content: formatMessage(messages.inheritedProject),
				tooltipMessage: formatMessage(messages.inheritedProjectTooltip),
				hideIcon: true,
				entryPointWrapper: (p) => (
					<EntryPointWrapper
						{...p}
						onClose={() => setIsPinned(false)}
						onInheritancePopupOpen={() => setIsPinned(true)}
						issueAri={issueAri}
						linkedProject={{
							projectUrl,
							isRestricted: initialHasNoAccess,
						}}
					/>
				),
			},
		];
	}, [issueAri, isInherited, formatMessage, createAnalyticsEvent, projectUrl, initialHasNoAccess]);

	if (projectUrl) {
		return (
			<Box
				ref={containerRef}
				testId="issue-field-atlas-project-common.ui.atlas-project-smart-link.container"
				xcss={!isInherited && styles.container}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				onMouseDown={() => {
					// this is covering an edge case where user clicks inherit button to close the popup, it won't lose focus so we have to help it a little
					if (isPinned) {
						setIsFocused(false);
					}
				}}
				onFocus={() => setIsFocused(true)}
				onBlur={(event: React.FocusEvent) => {
					// Check if focus is moving outside the container
					if (containerRef.current && !containerRef.current.contains(event.relatedTarget)) {
						setIsFocused(false);
					}
				}}
			>
				<SimpleInlineSmartLink
					anchorTarget="_blank"
					url={projectUrl}
					metadata={[]}
					actions={shouldShowActions ? actions : undefined}
					initialHasNoAccess={initialHasNoAccess}
					projectAri={projectAri}
					showNoAccessPopup={isInherited === false}
					isPrivate={isPrivate}
					onHandleNoAccess={onHandleNoAccess}
					text={fg('project_ssr_smart_card') ? name || undefined : undefined}
				/>
			</Box>
		);
	}

	// handle no project key returned
	return <RestrictedAlikeSmartLink />;
};
